import moment from 'moment';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { AnalyticsStatus, useAnalytics } from '@treams/treams-analytics';
import {
	SCORE_WINDOWS,
	scoreTranslation,
} from '@treams/treams-analytics/src/score/services/ScoreConfig';
import { useWorkspaceConfiguration } from '@treams/treams-hooks';
import { GroupsIcon, HistoryIcon, SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Card,
	Container,
	Empty,
	FlexGap,
	Heading3,
	type ITimeMachine,
	Input,
	Jumbo,
	SegmentedControl,
	Skeleton,
	createTimeMachine,
	tokens,
} from '@treams/treams-ui';

import { AnalyticsFilter } from '../components/AnalyticsFilter';
import { ScoreCategory, ScoreModuleState, type ScoreModuleStatus } from './TreamsScore.types';
import { TreamsScoreAnalyticsLoading } from './TreamsScoreAnalyticsLoading';
import { TreamsScoreCard } from './TreamsScoreCard/TreamsScoreCard';
import { TreamsScoreInsights } from './TreamsScoreCard/TreamsScoreInsights';
import { TreamsScoreComparisonTable } from './TreamsScoreTable/TreamsScoreComparisonTable';
import { SCORE_WEIGHTS } from './config/score-weights';
import { useScoreGroupFilter } from './hooks/useScoreGroupFilter';
import { useScoreModuleStatus } from './hooks/useScoreModuleStatus';
import { useTreamsScore } from './hooks/useTreamsScore';
import { toDisplayScore } from './scoreDisplay';
import { buildScoreInsightsInput, scoreInsightsCacheKey } from './scoreInsights';
import {
	buildSeriesEndDates,
	clampScoreEndDate,
	toRowTrends,
	toScoreSeriesPoints,
} from './scoreSeries';
import { adjustScoresForModuleStatus, aggregateRowScores } from './scoreTotal';

type ScoreGroupType = graphql.GroupType.Department | graphql.GroupType.Team;

const COMPARISON_SEARCH_MIN_ROWS = 10;

// Shared between the comparison control and the first table column: the column
// title follows the active segment by definition.
const GROUP_TYPE_LABEL_KEYS: Record<ScoreGroupType, string> = {
	[graphql.GroupType.Department]: 'analytics.treamsScore.column.departments',
	[graphql.GroupType.Team]: 'analytics.treamsScore.column.teams',
};

export function TreamsScoreAnalytics(props: { headerFooter?: ReactNode }) {
	const { t } = useTranslation();

	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(() =>
		createTimeMachine('quarter', new Date()),
	);

	const workspaceConfiguration = useWorkspaceConfiguration();

	const workspaceStartDate = useMemo(
		() =>
			workspaceConfiguration.data?.startDate
				? moment(workspaceConfiguration.data.startDate).toDate()
				: undefined,
		[workspaceConfiguration.data?.startDate],
	);

	const { endDate, seriesEndDates } = useMemo(() => {
		const now = new Date();

		return {
			endDate: clampScoreEndDate(timeMachine.current[1], now),
			seriesEndDates: buildSeriesEndDates(timeMachine, workspaceStartDate, now),
		};
	}, [timeMachine, workspaceStartDate]);

	const {
		rows: teams,
		series,
		isLoading: scoreLoading,
		engagementSurveyAvailable,
	} = useTreamsScore(endDate, seriesEndDates);

	const { status, loading: analyticsLoading } = useAnalytics();

	const analyticsUnavailable = !analyticsLoading && status !== AnalyticsStatus.Succeeded;

	// Keep the skeleton up while the score worker request is still in flight.
	const loading = analyticsLoading || (status === AnalyticsStatus.Succeeded && scoreLoading);

	const [groupType, setGroupType] = useState<ScoreGroupType>(graphql.GroupType.Department);
	const [comparisonQuery, setComparisonQuery] = useState('');

	const filter = useScoreGroupFilter(teams);

	const baseModuleStatus = useScoreModuleStatus();

	// A module that is switched off today should not blank out history: when a
	// qualifying survey was available in the selected window, engagement scores
	// like an enabled module; the disabled fallback (score 1) only applies when
	// no survey ran at that moment.
	const moduleStatus = useMemo<ScoreModuleStatus>(
		() =>
			baseModuleStatus[ScoreCategory.Engagement] === ScoreModuleState.Disabled &&
			engagementSurveyAvailable
				? { ...baseModuleStatus, [ScoreCategory.Engagement]: ScoreModuleState.Enabled }
				: baseModuleStatus,
		[baseModuleStatus, engagementSurveyAvailable],
	);

	const aggregatedScores = useMemo(
		() => aggregateRowScores(filter.filteredRows),
		[filter.filteredRows],
	);

	const hasData = useMemo(() => {
		return filter.filteredRows.length > 0;
	}, [filter.filteredRows]);

	const adjustedScores = useMemo(
		() => adjustScoresForModuleStatus(aggregatedScores, moduleStatus),
		[aggregatedScores, moduleStatus],
	);

	const periodStart = useMemo(
		() => moment(endDate).subtract(SCORE_WINDOWS.rollingWindowMonths, 'months').toDate(),
		[endDate],
	);

	const showDelta =
		filter.filteredRows.some((row) => row.comparisonAvailable !== false) &&
		(!workspaceStartDate || !moment(periodStart).isBefore(workspaceStartDate, 'day'));

	const adjustedRows = useMemo(
		() =>
			filter.filteredRows.map((row) => ({
				...row,
				scores: adjustScoresForModuleStatus(row.scores, moduleStatus),
			})),
		[filter.filteredRows, moduleStatus],
	);

	// The comparison control only narrows the table; the score card above stays
	// aggregated over the full (selection-filtered) set.
	const tableRows = useMemo(
		() => adjustedRows.filter((row) => row.type === groupType),
		[adjustedRows, groupType],
	);

	const showComparisonSearch = tableRows.length > COMPARISON_SEARCH_MIN_ROWS;

	const searchedRows = useMemo(() => {
		const query = comparisonQuery.trim().toLocaleLowerCase();
		if (!showComparisonSearch || !query) return tableRows;
		return tableRows.filter((row) => row.name.toLocaleLowerCase().includes(query));
	}, [tableRows, showComparisonSearch, comparisonQuery]);

	// Manager avatars aren't part of the score snapshot, so resolve them from
	// the live user directory. Fetch across both tabs so switching is instant.
	const managerIDs = useMemo(
		() => [...new Set(adjustedRows.flatMap((row) => row.managers.map((manager) => manager.id)))],
		[adjustedRows],
	);

	const { data: managerAvatarData } = graphql.useScoreManagerAvatarsQuery({
		variables: { ids: managerIDs },
		skip: managerIDs.length === 0,
	});

	const managerAvatars = useMemo(() => {
		const map = new Map<string, string>();
		managerAvatarData?.usersByIDs.forEach((user) => {
			if (user.avatar?.source.url) map.set(user.id, user.avatar.source.url);
		});
		return map;
	}, [managerAvatarData]);

	// The card line aggregates each history point exactly like the headline:
	// over the same selected rows, with the same module adjustment.
	const cardSeries = useMemo(
		() =>
			toScoreSeriesPoints(
				series,
				filter.filteredRows.map((row) => row.id),
				moduleStatus,
				timeMachine.interval,
			),
		[series, filter.filteredRows, moduleStatus, timeMachine.interval],
	);

	const tableTrends = useMemo(() => toRowTrends(series, moduleStatus), [series, moduleStatus]);

	const insightsSelectionName =
		filter.groupSelection.length === 1
			? filter.groupSelection[0].group.name
			: t(
					filter.hasExplicitGroupSelection
						? 'analytics.treamsScore.insights.selection'
						: 'analytics.treamsScore.insights.organisation',
				);

	const insightsInput = useMemo(
		() =>
			buildScoreInsightsInput({
				selection: insightsSelectionName,
				band: t(scoreTranslation(toDisplayScore(adjustedScores.total.score))),
				scores: adjustedScores,
				moduleStatus,
				weights: SCORE_WEIGHTS,
				trend: cardSeries.map((point) => point.value),
				comparisonAvailable: showDelta,
			}),
		[insightsSelectionName, adjustedScores, moduleStatus, cardSeries, showDelta, t],
	);

	const insightsCacheKey = useMemo(
		() => scoreInsightsCacheKey(filter.filteredRows),
		[filter.filteredRows],
	);

	return (
		<>
			<Jumbo title={t('analytics.treamsScore.pageTitle')} footer={props.headerFooter} wide={true} />

			<>
				<AnalyticsFilter
					departments={filter}
					canSelectGroup={filter.canSelect}
					onDepartmentChange={filter.updateExplicitGroupSelection}
					timeMachine={timeMachine}
					onTimeMachineChange={setTimeMachine}
					disableQuery
					intervals={['month', 'quarter', 'year']}
				/>

				<Container
					wide={true}
					py={4}
					maxHeight={!loading && (analyticsUnavailable || !hasData) ? '500px' : undefined}
					overflow={!loading && (analyticsUnavailable || !hasData) ? 'hidden' : undefined}
				>
					{loading ? (
						<TreamsScoreAnalyticsLoading />
					) : analyticsUnavailable || !hasData ? (
						<Box style={{ position: 'relative', height: '500px' }}>
							<Skeleton.Empty contents={null}>
								<TreamsScoreAnalyticsLoading />
							</Skeleton.Empty>
							<Box
								style={{
									position: 'absolute',
									bottom: 0,
									left: 0,
									width: '100%',
									height: '400px',
									background: `linear-gradient(to bottom, transparent, white)`,
									pointerEvents: 'none',
								}}
							/>
							<Box
								style={{
									zIndex: 4,
									position: 'absolute',
									left: '50%',
									top: '50%',
									transform: 'translate(-50%, -70%)',
								}}
							>
								{analyticsUnavailable ? (
									<Empty
										icon={<HistoryIcon />}
										title={t('analytics.cycles.notReady.title')}
										description={t('analytics.cycles.notReady.description')}
									/>
								) : (
									<Empty
										icon={<GroupsIcon />}
										title={t('analytics.treamsScore.empty.title')}
										description={t('analytics.treamsScore.empty.description')}
									/>
								)}
							</Box>
						</Box>
					) : (
						<>
							<TreamsScoreCard
								total={adjustedScores.total}
								series={cardSeries}
								periodStart={periodStart}
								showDelta={showDelta}
							>
								<TreamsScoreInsights cacheKey={insightsCacheKey} input={insightsInput} />
							</TreamsScoreCard>
							<Card mt={`${tokens.gap.section}px`} p={0}>
								<FlexGap
									flexDirection="column"
									alignItems="flex-start"
									gap={tokens.gap.group}
									p={`${tokens.padding.container}px`}
								>
									<Heading3>{t('analytics.treamsScore.teamComparison')}</Heading3>
									<FlexGap alignItems="center" gap={tokens.gap.list}>
										<SegmentedControl
											value={groupType}
											onChange={(value) => {
												setGroupType(value as ScoreGroupType);
												setComparisonQuery('');
											}}
											options={[
												{
													title: t(GROUP_TYPE_LABEL_KEYS[graphql.GroupType.Department]),
													value: graphql.GroupType.Department,
												},
												{
													title: t(GROUP_TYPE_LABEL_KEYS[graphql.GroupType.Team]),
													value: graphql.GroupType.Team,
												},
											]}
										/>
										{showComparisonSearch && (
											<Box width={240} flexShrink={0}>
												<Input
													search
													icon={<SearchIcon />}
													value={comparisonQuery}
													onChange={setComparisonQuery}
													placeholder={t('common.search')}
													clearable
												/>
											</Box>
										)}
									</FlexGap>
								</FlexGap>
								<TreamsScoreComparisonTable
									teams={searchedRows}
									managerAvatars={managerAvatars}
									trends={tableTrends}
									comparisonDate={periodStart}
									embedded
									nameColumnHeader={t(GROUP_TYPE_LABEL_KEYS[groupType])}
									showManagerColumn={groupType === graphql.GroupType.Department}
									emptyState={
										showComparisonSearch && comparisonQuery.trim()
											? {
													icon: <SearchIcon />,
													title: t('analytics.treamsScore.search.empty'),
												}
											: {
													icon: <GroupsIcon />,
													title: t('analytics.treamsScore.empty.title'),
													description: t('analytics.treamsScore.empty.description'),
												}
									}
								/>
							</Card>
						</>
					)}
				</Container>
			</>
		</>
	);
}
